import { convexQuery } from "@convex-dev/react-query";
import { formatDate } from "@plasma/utils/time";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { type ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";

import {
  type EmployeeTipEntry,
  employeesGetAll,
  locationSalesGetEmployeeTips,
} from "~/api-refs";
import { SalesDataTable } from "~/components/reports/sales-data-table";
import { LoadingPanel } from "~/components/shared/loading-panel";
import { Combobox } from "~/components/ui/combobox";
import { toConvexTableId } from "~/lib/convex-table-id";
import { optionalSearchString } from "~/lib/parse-enum-value";
import { useSearchParamsDateRange } from "~/utils/search-params-date-range";

export const Route = createFileRoute(
  "/_authenticated/reports/employee-tip-report"
)({
  component: EmployeeTipReportPage,
  validateSearch: (search: Record<string, unknown>) => ({
    location: optionalSearchString(search.location),
    employeeId: optionalSearchString(search.employeeId),
  }),
});

function EmployeeTipReportPage() {
  const navigate = useNavigate();
  const searchParams = useSearch({ strict: false });
  const locationId = searchParams.location;
  const employeeId = searchParams.employeeId;

  const { data: employeesResult } = useQuery(
    convexQuery(employeesGetAll, {
      paginationOpts: { numItems: 500, cursor: null },
    })
  );
  const employees =
    employeesResult?.page.map((e) => ({
      id: e.id,
      firstName: e.firstName,
      lastName: e.lastName,
    })) ?? [];

  if (!locationId) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <h3 className="subtitle text-foreground mb-2">Location Required</h3>
          <p className="body text-muted-foreground">
            Please select a location from the filters above to view Employee Tip
            Report.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="subtitle text-foreground mb-2">Employee Tip Report</h2>
        <p className="body text-muted-foreground">
          View detailed employee tip data and performance
        </p>
      </div>

      <div className="flex items-center gap-4">
        <Combobox
          className="bg-background h-9 w-48 min-w-0 rounded-md p-2"
          dropdownClassName="w-48"
          items={employees.map((e) => ({
            label: `${e.firstName} ${e.lastName}`,
            value: e.id,
            keywords: [e.firstName, e.lastName],
          }))}
          onValueChange={(v) => {
            void navigate({
              from: "/reports/employee-tip-report",
              search: (prev) => ({
                ...prev,
                employeeId: v ?? undefined,
              }),
            });
          }}
          placeholder="Select employee..."
          value={employeeId ?? null}
        />
      </div>

      {employeeId ? (
        <EmployeeTipReportContent
          employeeId={employeeId}
          locationId={locationId}
        />
      ) : (
        <div className="flex h-32 items-center justify-center">
          <div className="text-center">
            <h3 className="subtitle text-foreground mb-2">Select Employee</h3>
            <p className="body text-muted-foreground">
              Choose an employee from the dropdown above to view their tip
              report.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

type TipsData = EmployeeTipEntry;

const columnHelper = createColumnHelper<TipsData>();

const EmployeeTipReportContent = ({
  employeeId,
  locationId,
}: {
  employeeId: string;
  locationId: string;
}) => {
  const [dateRange] = useSearchParamsDateRange();

  const { data, isPending: isLoading } = useQuery(
    convexQuery(locationSalesGetEmployeeTips, {
      dateRange: {
        from: dateRange.from?.getTime() ?? 0,
        to: dateRange.to?.getTime() ?? 0,
      },
      employeeId: toConvexTableId("employees", employeeId),
      locationId: toConvexTableId("locations", locationId),
    })
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("date", {
          header: "Date",
          cell: ({ getValue }) => formatDate(new Date(getValue()), "M/D/YYYY"),
          meta: {
            getCellForExport: (d: TipsData["date"]) =>
              new Date(d).toISOString(),
          },
        }),
        columnHelper.accessor("amountSold", {
          header: "Amount Sold",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TipsData["amountSold"]) => d,
          },
        }),
        columnHelper.accessor("cashTips", {
          header: "Cash Tips",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TipsData["cashTips"]) => d,
          },
        }),
        columnHelper.accessor("creditTips", {
          header: "Credit Tips",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TipsData["creditTips"]) => d,
          },
        }),
        columnHelper.accessor("debitTips", {
          header: "Debit Tips",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TipsData["debitTips"]) => d,
          },
        }),
        columnHelper.accessor("totalTips", {
          header: "Total Tips",
          cell: ({ getValue }) =>
            getValue()?.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            }),
          meta: {
            getCellForExport: (d: TipsData["totalTips"]) => d,
          },
        }),
        columnHelper.display({
          id: "tipPercentage",
          header: "Tip %",
          cell: ({ row }) => {
            const amountSold = row.original.amountSold;
            const totalTips = row.original.totalTips;
            if (!amountSold || amountSold === 0) {
              return "0.0%";
            }
            const tipPercentage = (totalTips / amountSold) * 100;
            return `${tipPercentage.toFixed(1)}%`;
          },
          meta: {
            getCellForExport: (row: TipsData) => {
              const amountSold = row.amountSold;
              const totalTips = row.totalTips;
              if (!amountSold || amountSold === 0) {
                return 0;
              }
              return (totalTips / amountSold) * 100;
            },
          },
        }),
      ] as ColumnDef<TipsData>[],
    []
  );

  if (isLoading) {
    return <LoadingPanel message="Loading tips data..." />;
  }

  return (
    <SalesDataTable
      columns={columns}
      data={data ?? []}
      id="employee-tip-report-table"
    />
  );
};
